import { Box, Grid } from '@mui/material'
import { AlarmIcon, Approve, CloseIcon, LocationIcon } from 'assets/icons'
import IconButton from 'components/buttons/IconButton'
import InputWithApproveButton from 'components/inputs/InputWithApproveButton'
import PopupSectionRow from 'components/popup/PopupSectionRow'
import { firebaseConfig } from 'firebase-config'
import GoogleMapReact from 'google-map-react'
import { useTranslation } from 'react-i18next'
import { theme } from 'theme'
import { EventLocation } from 'types/api/changes'

import { TextWithLinks } from 'components/TextWithLinks'
import { IEventDetails } from 'hooks/useEventDetails'
import { ChangeEvent, useState } from 'react'

import './styles.scss'
import { calculateZoomLevel, radiusInMeters } from 'utils/location_reminder'
import ChangeTextDialog from 'components/dialogs/ChangeTextDialog'
import i18n from 'i18n'

interface EventLocationRowProps {
  disallowEdit?: boolean
  eventHook?: IEventDetails
  setLocation: (loc: EventLocation | undefined) => void
  eventLocation: EventLocation | undefined
  isUiBlocked?: boolean
}

export function EventLocationRow(props: EventLocationRowProps) {
  const hasLocation =
    typeof props.eventLocation !== 'string' &&
    props.eventLocation != undefined &&
    Object.keys(props.eventLocation).length > 0

  const removeLocation = () => {
    props.setLocation(undefined)
  }

  const { t } = useTranslation()

  const onEventLocationChange = (event: ChangeEvent<HTMLInputElement>) => {
    props.eventHook?.setLocationPendingText(event.target.value)
  }

  const locationReminder = props.eventHook?.locationReminder?.at(0)

  const [openChange, setOpenChange] = useState(false)

  return hasLocation ? (
    <div>
      <PopupSectionRow
        icon={<LocationIcon />}
        cursor={props.eventLocation?.Address ? 'default' : 'pointer'}
        valuePlaceholder={!props.eventLocation?.Address}
        clickableTitle
        title={
          <p
            id="event-location-title"
            color={'red'}
            style={{
              paddingLeft: i18n.dir() === 'ltr' ? '6px' : undefined,
              paddingRight: i18n.dir() === 'rtl' ? '6px' : undefined
            }}
          >
            {props.eventLocation?.Address ? (
              <TextWithLinks text={props.eventLocation?.Address} />
            ) : (
              t('addLocation')
            )}
          </p>
        }
        action={() => {
          if (!props.isUiBlocked) setOpenChange(true)
        }}
        value={
          props.disallowEdit ? (
            <></>
          ) : (
            <IconButton
              stopPropagationOnClick
              size="xs"
              onClick={removeLocation}
            >
              <CloseIcon />
            </IconButton>
          )
        }
      />
      {locationReminder ? (
        <PopupSectionRow
          icon={<AlarmIcon />}
          cursor="default"
          onlyTitle
          overflow="hidden"
          title={t(
            locationReminder.approachEnabled
              ? 'remindWhenApproaching'
              : 'remindWhenDepartingBy',
            {
              radius: locationReminder.radius,
              unit:
                locationReminder.lenType === '1'
                  ? t('meters')
                  : locationReminder.lenType === '2'
                    ? t('yards')
                    : locationReminder.lenType === '3'
                      ? t('miles')
                      : t('kilometers')
            }
          )}
        />
      ) : (
        <></>
      )}
      {props.eventLocation?.Lat && props.eventLocation.Long && (
        <SimpleMap
          height={'150px'}
          lat={parseFloat(props.eventLocation?.Lat ?? '0.0')}
          lng={parseFloat(props.eventLocation?.Long ?? '0.0')}
          radius={
            props.eventHook?.locationReminder &&
            props.eventHook.locationReminder.length > 0
              ? radiusInMeters(
                  props.eventHook.locationReminder[0].lenType,
                  props.eventHook.locationReminder[0].radius
                )
              : 0
          }
        />
      )}
      <ChangeTextDialog
        open={openChange}
        setOpen={setOpenChange}
        title={t('addLocation')}
        text={props.eventLocation?.Address ?? ''}
        onApprove={(s: string) => {
          props.setLocation(undefined)
          props.eventHook?.addLocationToEvent(s)
        }}
        placeholder={t('addLocation')}
      />
    </div>
  ) : (
    <InputWithApproveButton
      onTextChange={onEventLocationChange}
      inputStyle={{}}
      stopPropagationOnClick
      leftIcon={<LocationIcon color={theme.palette.input.placeholder} />}
      hintText={`${t('addLocation')}`}
      onApprove={props.eventHook?.addLocationToEvent ?? (() => {})}
      approveIcon={<Approve style={{ marginLeft: 8, marginRight: 8 }} />}
      id="input-add-location"
    />
  )
}

interface SimpleMapProps {
  height: string
  lat: number
  lng: number
  radius?: number
}

export function SimpleMap(props: SimpleMapProps) {
  if (props.lat === 0 && props.lng === 0) return <></>

  const defaultProps = {
    center: {
      lat: props.lat,
      lng: props.lng
    },

    zoom: calculateZoomLevel(props.radius ?? 0)
  }

  const handleApiLoaded = (map: any, maps: any) => {
    // Create a new circle and attach it to the map
    new maps.Circle({
      strokeColor: '#0084d3',

      strokeWeight: 3,
      fillColor: '#500084d3',
      map,
      center: { lat: props.lat, lng: props.lng },
      radius: props.radius ?? 0
    })
  }

  return (
    <Grid
      container
      style={{
        height: props.height,
        width: '100%'
      }}
    >
      <GoogleMapReact
        options={{
          fullscreenControl: false,
          mapTypeControl: false,
          streetViewControl: false,
          zoomControl: false
        }}
        bootstrapURLKeys={{ key: firebaseConfig.apiKey || '' }}
        yesIWantToUseGoogleMapApiInternals
        defaultCenter={defaultProps.center}
        defaultZoom={defaultProps.zoom}
        onGoogleApiLoaded={({ map, maps }) => handleApiLoaded(map, maps)}
      />
    </Grid>
  )
}
